import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Grid } from "@mui/material";
import Button from "@mui/material/Button";
import { darken, lighten, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { v4 as uuid } from "uuid";

import OmniAvatar from "../ui/omniAvatar";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const PatientJournalEntryDialog = props => {
  const {
    open,
    dialogTitle = "Update Journal",
    dialogMessage = null,
    textLabel = "Entry",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    deleteMessage = "delete",
    maxWidth = "sm",
    fullWidth = true,
    rowCount = 8,
    symptomData = null,
    journalEntry,
  } = props;

  const [showConfirm, setShowConfirm] = useState(false);
  const [value, setValue] = useState(journalEntry.note);
  const [inputValid, setInputValid] = useState(journalEntry.note.length ? true : false);

  const handleChange = event => {
    const newValue = `${event.target.value}`;
    setValue(newValue);
    setInputValid(newValue.length ? true : false);
  };

  const onDeleteHandler = () => {
    props.onDelete(journalEntry);
  };

  const onAcceptHandler = () => {
    const newEntry = {
      id: journalEntry.id || uuid(),
      date: journalEntry.date,
      note: value,
    };
    props.onAccept(newEntry);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container direction="row" spacing={2}>
          <Grid
            container
            item
            xs={6}
            direction="column"
            justifyContent="space-around"
            alignItems="stretch"
          >
            {dialogMessage && (
              <Grid item>
                <DialogMessage>{dialogMessage}</DialogMessage>
              </Grid>
            )}
            <Grid item>
              <OmniTextField
                label={textLabel}
                type="textInput"
                name={uuid()}
                variant="outlined"
                value={value}
                onChange={handleChange}
                autofill="off"
                autoFocus
                placeholder={textLabel}
                multiline
                rowCount={rowCount}
              />
            </Grid>
          </Grid>
          <Grid container item direction="column" xs={6}>
            <Grid item>
              <DialogMessage>Symptoms</DialogMessage>
            </Grid>
            {symptomData && (
              <Grid
                container
                item
                xs={4}
                direction="row"
                rowSpacing={1}
                columnSpacing={1}
                justifyContent="flex-start"
              >
                {symptomData
                  .sort((a, b) => b.value - a.value)
                  .map((symptom, index) => {
                    return (
                      <>
                        <Grid item xs={4} sm={3} key={"value" + symptom.name + index}>
                          <OmniAvatar
                            sx={[
                              {
                                width: 28,
                                height: 28,
                                fontSize: "1.2rem",
                                lineHeight: "1.2rem",
                                alignItems: "center",
                                paddingTop: "1px",
                              },
                              symptom.value >= 7 && {
                                color: darken(theme.palette.error.main, 0.5),
                                backgroundColor: lighten(theme.palette.error.main, 0.5),
                                border: `2px solid ${darken(theme.palette.error.main, 0.5)}`,
                              },
                              symptom.value > 3 &&
                                symptom.value < 7 && {
                                  color: darken(theme.palette.warning.main, 0.5),
                                  backgroundColor: lighten(theme.palette.warning.main, 0.5),
                                  border: `2px solid ${darken(theme.palette.warning.main, 0.5)}`,
                                },
                              symptom.value <= 3 && {
                                color: darken(theme.palette.success.main, 0.5),
                                backgroundColor: lighten(theme.palette.success.main, 0.5),
                                border: `2px solid ${darken(theme.palette.success.main, 0.5)}`,
                              },
                            ]}
                          >
                            {symptom.value}
                          </OmniAvatar>
                        </Grid>
                        <Grid item xs={8} sm={9}>
                          <Typography
                            variant="overline"
                            key={"value2" + symptom.name + index}
                            sx={{ lineHeight: "1em" }}
                          >
                            {symptom.name}
                          </Typography>
                        </Grid>
                      </>
                    );
                  })}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {(journalEntry.note.length ? true : false) && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={!inputValid}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this entry?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default PatientJournalEntryDialog;
